import React, { useState, useEffect, useRef } from 'react';
import { ClientEvent } from '../../data/clientEventsData';
import { GalleryImage } from '../../services/imageService';

interface EventGalleryModalProps {
  isOpen: boolean;
  selectedEvent: ClientEvent | null;
  images: GalleryImage[];
  loadingImages: boolean;
  onClose: () => void;
}

const EventGalleryModal: React.FC<EventGalleryModalProps> = ({
  isOpen,
  selectedEvent,
  images,
  loadingImages,
  onClose,
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  const handleNextImage = () => {
    if (images.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }
  };

  const handlePrevImage = () => {
    if (images.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowRight') {
      handleNextImage();
    } else if (e.key === 'ArrowLeft') {
      handlePrevImage();
    }
  };

  // Reset image index when modal opens or images change
  useEffect(() => {
    if (isOpen) {
      setCurrentImageIndex(0);
    }
  }, [isOpen, images]);

  // Control body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !selectedEvent) {
    return null;
  }

  return (
    <div
      className="gallery-modal-overlay"
      onClick={onClose}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <div className="gallery-modal" onClick={e => e.stopPropagation()}>
        <button
          className="modal-close"
          onClick={onClose}
          aria-label="Cerrar galería"
        >
          ✕
        </button>

        <div className="modal-header">
          <div className="modal-company">
            <span className="modal-logo">{selectedEvent.logo}</span>
            <div>
              <h3>{selectedEvent.company}</h3>
              <p className="event-name">{selectedEvent.description}</p>
            </div>
          </div>
        </div>

        <div className="carousel-container">
          <button
            className="carousel-btn prev"
            onClick={handlePrevImage}
            aria-label="Imagen anterior"
          >
            ‹
          </button>

          <div className="carousel-content" ref={carouselRef}>
            {loadingImages ? (
              <div className="loading-images">
                <div className="spinner"></div>
                <p>Cargando imágenes...</p>
              </div>
            ) : images.length > 0 ? (
              <div className="carousel-image-container">
                <img
                  src={images[currentImageIndex].url}
                  alt={images[currentImageIndex].alt}
                  className="carousel-image"
                  onError={(e) => {
                    e.currentTarget.src = `https://via.placeholder.com/800x600/4A5568/FFFFFF?text=${selectedEvent?.company}+${currentImageIndex + 1}`;
                  }}
                />
              </div>
            ) : (
              <div className="no-images">
                <span className="no-images-icon">📷</span>
                <p>No hay imágenes disponibles para este proyecto</p>
                <p className="no-images-hint">
                  Las imágenes se pueden agregar desde el panel de administración
                </p>
              </div>
            )}
          </div>

          <button
            className="carousel-btn next"
            onClick={handleNextImage}
            aria-label="Siguiente imagen"
          >
            ›
          </button>
        </div>

        <div className="carousel-indicators">
          {images.map((_, index: number) => (
            <button
              key={index}
              className={`indicator ${index === currentImageIndex ? 'active' : ''}`}
              onClick={() => setCurrentImageIndex(index)}
              aria-label={`Ir a imagen ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// Add styles component
const styles = `
<style>
.gallery-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(26, 32, 44, 0.95);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: calc(var(--industrial-unit) * 2);
  animation: fadeIn 0.3s ease;
}

.gallery-modal {
  background: var(--industrial-white);
  border-radius: calc(var(--industrial-border-radius) * 3);
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  position: relative;
  animation: slideInUp 0.3s ease;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-close {
  position: absolute;
  top: calc(var(--industrial-unit) * 2);
  right: calc(var(--industrial-unit) * 2);
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: var(--industrial-white);
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 10;
  transition: var(--industrial-transition);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-close:hover {
  background: rgba(0, 0, 0, 0.7);
  transform: scale(1.1);
}

.modal-header {
  background: linear-gradient(135deg, var(--industrial-dark), #2D3748);
  color: var(--industrial-white);
  padding: calc(var(--industrial-unit) * 4);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-company {
  display: flex;
  align-items: center;
  gap: calc(var(--industrial-unit) * 2);
}

.modal-logo {
  font-size: 2.5rem;
  background: var(--industrial-white);
  color: var(--industrial-dark);
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-company h3 {
  font-family: var(--industrial-font-heading);
  font-size: 1.5rem;
  margin: 0 0 4px 0;
  color: var(--industrial-white);
  line-height: 1.2;
}

.event-name {
  font-family: var(--industrial-font-secondary);
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  line-height: 1.4;
}

.carousel-container {
  position: relative;
  background: var(--industrial-off-white);
  min-height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.carousel-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.7);
  color: var(--industrial-white);
  border: none;
  font-size: 2rem;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  cursor: pointer;
  z-index: 10;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.carousel-btn:hover {
  background: rgba(0, 0, 0, 0.9);
  transform: translateY(-50%) scale(1.1);
}

.carousel-btn.prev {
  left: calc(var(--industrial-unit) * 2);
}

.carousel-btn.next {
  right: calc(var(--industrial-unit) * 2);
}

.carousel-content {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.carousel-image-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.carousel-image {
  max-width: 100%;
  max-height: 400px;
  object-fit: contain;
  border-radius: var(--industrial-border-radius);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
}

.image-caption {
  margin-top: calc(var(--industrial-unit) * 3);
  text-align: center;
  padding: 0 calc(var(--industrial-unit) * 4);
}

.image-caption p {
  font-family: var(--industrial-font-secondary);
  font-size: 1rem;
  color: var(--industrial-metallic);
  margin: 0;
  line-height: 1.6;
}

.loading-images {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--industrial-metallic);
  padding: calc(var(--industrial-unit) * 8);
  font-family: var(--industrial-font-secondary);
  gap: calc(var(--industrial-unit) * 2);
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid rgba(74, 85, 104, 0.2);
  border-top-color: var(--industrial-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.no-images {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--industrial-metallic);
  padding: calc(var(--industrial-unit) * 8);
  font-family: var(--industrial-font-secondary);
  gap: calc(var(--industrial-unit) * 1);
}

.no-images-icon {
  font-size: 4rem;
  margin-bottom: calc(var(--industrial-unit) * 2);
}

.no-images p {
  margin: 0;
  text-align: center;
}

.no-images-hint {
  font-size: 0.9rem;
  color: rgba(74, 85, 104, 0.7);
}

.carousel-indicators {
  display: flex;
  justify-content: center;
  gap: calc(var(--industrial-unit) * 1);
  padding: calc(var(--industrial-unit) * 3);
  background: var(--industrial-off-white);
}

.indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: rgba(74, 85, 104, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;
}

.indicator.active {
  background: var(--industrial-primary);
  transform: scale(1.2);
}

.indicator:hover {
  background: var(--industrial-accent);
  transform: scale(1.1);
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@media (max-width: 768px) {
  .gallery-modal {
    max-width: 95vw;
    max-height: 95vh;
  }

  .modal-header {
    padding: calc(var(--industrial-unit) * 3);
    flex-direction: column;
    gap: calc(var(--industrial-unit) * 2);
    text-align: center;
  }

  .modal-company {
    flex-direction: column;
    text-align: center;
  }

  .carousel-container {
    min-height: 300px;
  }

  .carousel-btn {
    width: 40px;
    height: 40px;
    font-size: 1.5rem;
  }

  .carousel-btn.prev {
    left: calc(var(--industrial-unit) * 1);
  }

  .carousel-btn.next {
    right: calc(var(--industrial-unit) * 1);
  }

  .carousel-image {
    max-height: 250px;
  }

  .image-caption {
    padding: 0 calc(var(--industrial-unit) * 2);
  }

  .image-caption p {
    font-size: 0.9rem;
  }
}

@media (max-width: 480px) {
  .carousel-container {
    min-height: 250px;
  }

  .carousel-image {
    max-height: 200px;
  }
}
</style>
`;

// Inject styles
if (typeof document !== 'undefined') {
  let styleElement = document.getElementById('event-gallery-modal-styles');
  if (!styleElement) {
    const style = document.createElement('style');
    style.id = 'event-gallery-modal-styles';
    style.innerHTML = styles.replace('<style>', '').replace('</style>', '');
    document.head.appendChild(style);
  } else {
    // Update existing styles to ensure latest changes are applied
    styleElement.innerHTML = styles.replace('<style>', '').replace('</style>', '');
  }
}

export default EventGalleryModal;
